import { useState, useRef, useCallback } from 'react'
import SnapZones from './SnapZones'
import { getMinWindowSize } from '../lib/windowGeometry'
import ExplorerApp from '../apps/ExplorerApp'
import BrowserApp from '../apps/BrowserApp'
import StoreApp from '../apps/StoreApp'
import MailApp from '../apps/MailApp'
import YouTubeApp from '../apps/YouTubeApp'
import CalculatorApp from '../apps/CalculatorApp'
import NotepadApp from '../apps/NotepadApp'
import WeatherApp from '../apps/WeatherApp'
import CalendarApp from '../apps/CalendarApp'
import SettingsApp from '../apps/SettingsApp'
import PhotoViewerApp from '../apps/PhotoViewerApp'
import SpotifyApp from '../apps/SpotifyApp'
import TetrisApp from '../apps/TetrisApp'
import SnakeApp from '../apps/SnakeApp'
import GamesApp from '../apps/GamesApp'
import AppsApp from '../apps/AppsApp'
import CrumbApp from '../apps/CrumbApp'
import ColorPickerApp from '../apps/ColorPickerApp'
import NewsApp from '../apps/NewsApp'
import TerminalApp from '../apps/TerminalApp'
import MinesweeperApp from '../apps/MinesweeperApp'
import TicTacToeApp from '../apps/TicTacToeApp'
import ArkanoidApp from '../apps/ArkanoidApp'
import ChessApp from '../apps/ChessApp'
import SpaceInvadersApp from '../apps/SpaceInvadersApp'

const Window = ({ window, onClose, onFocus, onMinimize, onMaximize, onUpdatePosition, onUpdateSize, onOpenApp, onDragStateChange }) => {
  const [isDragging, setIsDragging] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [resizeDirection, setResizeDirection] = useState('')
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const windowRef = useRef(null)
  
  // Store initial resize state in ref to avoid stale closures
  const resizeInitialState = useRef({ width: 0, height: 0, x: 0, y: 0, startX: 0, startY: 0 })
  
  const getAppTitle = (name) => {
    const titles = {
      explorer: 'Files',
      browser: 'Web Browser',
      store: 'Pink Pixel Catalog',
      catalog: 'Pink Pixel Catalog',
      mail: 'Mail',
      youtube: 'YouTube',
      calculator: 'Calculator',
      notepad: 'Notepad',
      weather: 'Weather',
      calendar: 'Calendar',
      settings: 'Settings',
      photos: 'Photos',
      spotify: 'Spotify',
      tetris: 'Tetris',
      snake: 'Snake',
      games: 'Game Center',
      apps: 'Applications',
      crumb: 'Chat with Crumb',
      colorpicker: 'Color Picker',
      news: 'Developer News',
      terminal: 'Terminal',
      minesweeper: 'Minesweeper',
      tictactoe: 'Tic-Tac-Toe',
      arkanoid: 'Arkanoid',
      chess: 'Chess',
      spaceinvaders: 'Space Invaders'
    }
    return titles[name] || 'Application'
  }

  const renderAppContent = () => {
    switch (window.appName) {
      case 'explorer':
        return <ExplorerApp onOpenApp={onOpenApp} />
      case 'browser':
        return <BrowserApp />
      case 'store':
      case 'catalog':
        return <StoreApp onOpenApp={onOpenApp} />
      case 'mail':
        return <MailApp />
      case 'youtube':
        return <YouTubeApp />
      case 'calculator':
        return <CalculatorApp />
      case 'notepad':
        return <NotepadApp />
      case 'weather':
        return <WeatherApp />
      case 'calendar':
        return <CalendarApp />
      case 'settings':
        return <SettingsApp />
      case 'photos':
        return <PhotoViewerApp />
      case 'spotify':
        return <SpotifyApp />
      case 'tetris':
        return <TetrisApp />
      case 'snake':
        return <SnakeApp />
      case 'minesweeper':
        return <MinesweeperApp />
      case 'tictactoe':
        return <TicTacToeApp />
      case 'arkanoid':
        return <ArkanoidApp />
      case 'chess':
        return <ChessApp />
      case 'spaceinvaders':
        return <SpaceInvadersApp />
      case 'games':
        return <GamesApp onOpenApp={onOpenApp} />
      case 'apps':
        return <AppsApp onOpenApp={onOpenApp} />
      case 'crumb':
        return <CrumbApp onClose={() => onClose(window.id)} />
      case 'colorpicker':
        return <ColorPickerApp />
      case 'news':
        return <NewsApp />
      case 'terminal':
        return <TerminalApp />
      default:
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <i className="fas fa-info-circle text-blue-400 text-4xl mb-4"></i>
              <p>Application content not available</p>
            </div>
          </div>
        )
    }
  }

  // Dragging and resizing both use pointer events rather than mouse events, so
  // the same code path serves mouse, touch and pen. The browser routes every
  // event for a captured pointer back to the element that captured it, which
  // replaces the old document-level listeners: no global state to clean up,
  // and the drag keeps working even when the pointer outruns the title bar.
  const dragPointer = useRef(null)

  // Ignore secondary mouse buttons and any second finger that lands mid-drag.
  const isPrimaryPointer = (e) =>
    e.isPrimary && (e.pointerType !== 'mouse' || e.button === 0)

  const handleDragStart = (e) => {
    if (e.target.closest('.window-controls')) return // Don't drag from window controls
    if (e.target.closest('.resize-handle')) return // Don't drag from resize handles
    if (window.isMaximized) return // Don't allow dragging when maximized
    if (!isPrimaryPointer(e)) return

    e.currentTarget.setPointerCapture(e.pointerId)

    dragPointer.current = {
      id: e.pointerId,
      offsetX: e.clientX - window.position.x,
      offsetY: e.clientY - window.position.y
    }

    setIsDragging(true)
    setDragOffset({ x: dragPointer.current.offsetX, y: dragPointer.current.offsetY })
    onFocus(window.id)

    // Update global drag state for snap zones (only for focused window)
    if (window.isFocused && onDragStateChange) {
      onDragStateChange({ isDragging: true, mousePosition: { x: e.clientX, y: e.clientY } })
    }
  }

  const handleDragMove = (e) => {
    const drag = dragPointer.current
    if (!drag || drag.id !== e.pointerId) return

    const newX = Math.max(0, Math.min(globalThis.innerWidth - window.size.width, e.clientX - drag.offsetX))
    const newY = Math.max(0, Math.min(globalThis.innerHeight - window.size.height, e.clientY - drag.offsetY))

    onUpdatePosition(window.id, { x: newX, y: newY })

    // Update pointer position for snap zones
    if (window.isFocused && onDragStateChange) {
      onDragStateChange({ isDragging: true, mousePosition: { x: e.clientX, y: e.clientY } })
    }
  }

  // Covers pointerup and pointercancel. Cancel matters on touch, where the
  // browser can take the pointer away mid-drag; without handling it the window
  // would stay stuck to the finger.
  const handleDragEnd = (e) => {
    const drag = dragPointer.current
    if (!drag || drag.id !== e.pointerId) return

    dragPointer.current = null
    setIsDragging(false)

    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }

    // Clear drag state for snap zones. SnapZones applies the snap when
    // isDragging goes false while a zone is active, so this is what commits it.
    if (onDragStateChange) {
      onDragStateChange({ isDragging: false, mousePosition: null })
    }
  }

  const handleResizeStart = useCallback((direction, e) => {
    e.stopPropagation()
    if (!isPrimaryPointer(e)) return

    e.currentTarget.setPointerCapture(e.pointerId)

    // Store initial state
    resizeInitialState.current = {
      pointerId: e.pointerId,
      direction,
      width: window.size.width,
      height: window.size.height,
      x: window.position.x,
      y: window.position.y,
      startX: e.clientX,
      startY: e.clientY
    }

    setIsResizing(true)
    setResizeDirection(direction)
    onFocus(window.id)
  }, [window.id, window.size, window.position, onFocus])

  const handleResizeMove = useCallback((e) => {
    const initial = resizeInitialState.current
    if (!initial || initial.pointerId !== e.pointerId) return

    const { direction } = initial
    const deltaX = e.clientX - initial.startX
    const deltaY = e.clientY - initial.startY

    let newWidth = initial.width
    let newHeight = initial.height
    let newX = initial.x
    let newY = initial.y

    // Minimum and maximum window size constraints. The floor is derived from
    // the viewport rather than fixed, because a fixed 300x200 is bigger than
    // some phone screens once the taskbar is accounted for, and the clamp then
    // fights itself.
    const { minWidth, minHeight } = getMinWindowSize()
    const maxWidth = globalThis.innerWidth || 1920
    const maxHeight = globalThis.innerHeight || 1080

    // Calculate new dimensions based on direction
    if (direction.includes('right')) {
      newWidth = Math.max(minWidth, Math.min(maxWidth - initial.x, initial.width + deltaX))
    }
    if (direction.includes('left')) {
      const proposedWidth = initial.width - deltaX
      newWidth = Math.max(minWidth, proposedWidth)
      // Only update X position if we're actually changing the width
      if (proposedWidth >= minWidth && initial.x + deltaX >= 0) {
        newX = initial.x + deltaX
      } else {
        // Clamp to minimum width and adjust position accordingly
        newX = Math.max(0, initial.x + initial.width - newWidth)
      }
    }
    if (direction.includes('bottom')) {
      newHeight = Math.max(minHeight, Math.min(maxHeight - initial.y, initial.height + deltaY))
    }
    if (direction.includes('top')) {
      const proposedHeight = initial.height - deltaY
      newHeight = Math.max(minHeight, proposedHeight)
      // Only update Y position if we're actually changing the height
      if (proposedHeight >= minHeight && initial.y + deltaY >= 0) {
        newY = initial.y + deltaY
      } else {
        // Clamp to minimum height and adjust position accordingly
        newY = Math.max(0, initial.y + initial.height - newHeight)
      }
    }

    // Ensure window doesn't go off-screen
    newX = Math.max(0, Math.min(maxWidth - newWidth, newX))
    newY = Math.max(0, Math.min(maxHeight - newHeight, newY))

      // Batch update both size and position to avoid state race conditions
    onUpdateSize(window.id, { width: newWidth, height: newHeight })
    if (newX !== window.position.x || newY !== window.position.y) {
      onUpdatePosition(window.id, { x: newX, y: newY })
    }
  }, [window.id, window.position, onUpdateSize, onUpdatePosition])

  const handleResizeEnd = useCallback((e) => {
    const initial = resizeInitialState.current
    if (!initial || initial.pointerId !== e.pointerId) return

    resizeInitialState.current = null
    setIsResizing(false)
    setResizeDirection('')

    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
  }, [])

  const windowStyle = {
    position: 'absolute',
    left: window.isMaximized ? 0 : window.position.x,
    top: window.isMaximized ? 0 : window.position.y,
    width: window.isMaximized ? '100vw' : window.size.width,
    height: window.isMaximized ? '100vh' : window.size.height,
    zIndex: window.zIndex,
    display: window.isMinimized ? 'none' : 'flex'
  }

  return (
    <div 
      ref={windowRef}
      style={windowStyle}
      className={`glass-effect ${window.isMaximized ? 'rounded-none' : 'rounded-2xl'} overflow-hidden flex-col ${
        window.isFocused ? 'ring-1 ring-blue-400/50' : ''
      } ${isDragging ? 'cursor-move' : ''} ${isResizing ? 'select-none' : ''}`}
      onClick={() => onFocus(window.id)}
    >
      {/* Window Title Bar */}
      <div 
        className={`window-titlebar bg-white/10 h-10 flex items-center px-4 justify-between select-none ${
          window.isMaximized ? 'cursor-default' : 'cursor-move'
        }`}
        onPointerDown={handleDragStart}
        onPointerMove={handleDragMove}
        onPointerUp={handleDragEnd}
        onPointerCancel={handleDragEnd}
        onContextMenu={(e) => {
          e.preventDefault()
          e.stopPropagation()
          const contextMenuEvent = new CustomEvent('showContextMenu', {
            detail: { event: e, type: 'window', data: { window } }
          })
          document.dispatchEvent(contextMenuEvent)
        }}
      >
        <span className="text-sm font-medium">{getAppTitle(window.appName)}</span>
        <div className="flex space-x-2 window-controls">
          <button 
            className="w-6 h-6 flex items-center justify-center hover:bg-white/20 rounded transition-colors"
            onClick={(e) => {
              e.stopPropagation()
              onMinimize(window.id)
            }}
          >
            <i className="fas fa-minus text-xs"></i>
          </button>
          <button 
            className="w-6 h-6 flex items-center justify-center hover:bg-white/20 rounded transition-colors"
            onClick={(e) => {
              e.stopPropagation()
              onMaximize(window.id)
            }}
          >
            <i className="fas fa-square text-xs"></i>
          </button>
          <button 
            className="w-6 h-6 flex items-center justify-center hover:bg-red-500/50 rounded transition-colors" 
            onClick={(e) => {
              e.stopPropagation()
              onClose(window.id)
            }}
          >
            <i className="fas fa-times text-xs"></i>
          </button>
        </div>
      </div>
      
      {/* Window Content */}
      <div className={`flex-1 overflow-auto ${window.appName === 'terminal' ? '' : 'p-6'}`}>
        {renderAppContent()}
      </div>
      
      {/* Resize Handles - Only show if not maximized */}
      {!window.isMaximized && (
        <>
          {/* Corner Handles */}
          <div 
            className="resize-handle resize-handle-corner absolute top-0 left-0 w-4 h-4 cursor-nw-resize group hover:bg-blue-400/30 rounded-tl-lg" 
            onPointerDown={(e) => handleResizeStart('top-left', e)}
            onPointerMove={handleResizeMove}
            onPointerUp={handleResizeEnd}
            onPointerCancel={handleResizeEnd}
          >
            <div className="absolute top-1 left-1 w-2 h-2 bg-blue-400/0 group-hover:bg-blue-400/60 rounded transition-colors" />
          </div>
          <div 
            className="resize-handle resize-handle-corner absolute top-0 right-0 w-4 h-4 cursor-ne-resize group hover:bg-blue-400/30 rounded-tr-lg" 
            onPointerDown={(e) => handleResizeStart('top-right', e)}
            onPointerMove={handleResizeMove}
            onPointerUp={handleResizeEnd}
            onPointerCancel={handleResizeEnd}
          >
            <div className="absolute top-1 right-1 w-2 h-2 bg-blue-400/0 group-hover:bg-blue-400/60 rounded transition-colors" />
          </div>
          <div 
            className="resize-handle resize-handle-corner absolute bottom-0 left-0 w-4 h-4 cursor-sw-resize group hover:bg-blue-400/30 rounded-bl-lg" 
            onPointerDown={(e) => handleResizeStart('bottom-left', e)}
            onPointerMove={handleResizeMove}
            onPointerUp={handleResizeEnd}
            onPointerCancel={handleResizeEnd}
          >
            <div className="absolute bottom-1 left-1 w-2 h-2 bg-blue-400/0 group-hover:bg-blue-400/60 rounded transition-colors" />
          </div>
          <div 
            className="resize-handle resize-handle-corner absolute bottom-0 right-0 w-4 h-4 cursor-se-resize group hover:bg-blue-400/30 rounded-br-lg" 
            onPointerDown={(e) => handleResizeStart('bottom-right', e)}
            onPointerMove={handleResizeMove}
            onPointerUp={handleResizeEnd}
            onPointerCancel={handleResizeEnd}
          >
            <div className="absolute bottom-1 right-1 w-2 h-2 bg-blue-400/0 group-hover:bg-blue-400/60 rounded transition-colors" />
          </div>
          
          {/* Edge Handles */}
          <div 
            className="resize-handle absolute top-0 left-4 right-4 h-2 cursor-n-resize hover:bg-blue-400/20" 
            onPointerDown={(e) => handleResizeStart('top', e)}
            onPointerMove={handleResizeMove}
            onPointerUp={handleResizeEnd}
            onPointerCancel={handleResizeEnd}
          />
          <div 
            className="resize-handle absolute bottom-0 left-4 right-4 h-2 cursor-s-resize hover:bg-blue-400/20" 
            onPointerDown={(e) => handleResizeStart('bottom', e)}
            onPointerMove={handleResizeMove}
            onPointerUp={handleResizeEnd}
            onPointerCancel={handleResizeEnd}
          />
          <div 
            className="resize-handle absolute left-0 top-4 bottom-4 w-2 cursor-w-resize hover:bg-blue-400/20" 
            onPointerDown={(e) => handleResizeStart('left', e)}
            onPointerMove={handleResizeMove}
            onPointerUp={handleResizeEnd}
            onPointerCancel={handleResizeEnd}
          />
          <div 
            className="resize-handle absolute right-0 top-4 bottom-4 w-2 cursor-e-resize hover:bg-blue-400/20" 
            onPointerDown={(e) => handleResizeStart('right', e)}
            onPointerMove={handleResizeMove}
            onPointerUp={handleResizeEnd}
            onPointerCancel={handleResizeEnd}
          />
        </>
      )}
    </div>
  )
}

const WindowManager = ({ windows, onClose, onFocus, onMinimize, onMaximize, onUpdatePosition, onUpdateSize, onOpenApp, onSnapWindow }) => {
  const [globalDragState, setGlobalDragState] = useState({ isDragging: false, mousePosition: null })

  // Handle snap preview (visual feedback)
  const handleSnapPreview = (snapZone) => {
    // Visual feedback is handled by SnapZones component
    // We could add additional feedback here if needed
  }

  // Handle snap release (apply the snap)
  const handleSnapRelease = (snapZone) => {
    if (snapZone && onSnapWindow) {
      const focusedWindow = windows.find(w => w.isFocused)
      if (focusedWindow) {
        onSnapWindow(focusedWindow.id, snapZone)
      }
    }
  }

  return (
    <div className="absolute inset-0 pointer-events-none">
      {windows.map((window) => (
        <div key={window.id} className="pointer-events-auto">
          <Window 
            window={window}
            onClose={onClose}
            onFocus={onFocus}
            onMinimize={onMinimize}
            onMaximize={onMaximize}
            onUpdatePosition={onUpdatePosition}
            onUpdateSize={onUpdateSize}
            onOpenApp={onOpenApp}
            onDragStateChange={setGlobalDragState}
          />
        </div>
      ))}
      
      {/* Global Snap Zones - only show for focused window */}
      <SnapZones
        isDragging={globalDragState.isDragging}
        mousePosition={globalDragState.mousePosition}
        onSnapPreview={handleSnapPreview}
        onSnapRelease={handleSnapRelease}
      />
    </div>
  )
}

export default WindowManager